import { IRowNode } from "@ag-grid-community/core";
import { Box, Flex, HStack, IconButton, IconProps } from "@chakra-ui/react";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import {
  DataTableColumnContext,
  DataTableContext,
} from "shared/components/table/data/DataTableContext";
import {
  DataTableActionInfo,
  DataTableOnButtonClickFn,
} from "shared/components/table/data/DataTableTypes";
import { EditIcon } from "shared/icons/EditIcon";
import { TrashIcon } from "shared/icons/TrashIcon";

export interface DataTableCellActionsProps {
  tableContext: DataTableContext;
  columnContext: DataTableColumnContext;
  cell?: GenericTableCell;
  row?: IRowNode<GenericTableRow>;
  actions: DataTableActionInfo;
  onButtonClick: DataTableOnButtonClickFn | undefined;
}

export const DataTableCellActions: React.FC<
  React.PropsWithChildren<DataTableCellActionsProps>
> = ({
  tableContext,
  columnContext,
  cell,
  row,
  actions,
  onButtonClick,
  children,
}) => (
  <Flex flexDirection="row" alignItems="center" flexGrow="1" gap="4px">
    <Box
      sx={{
        flexGrow: 1,
        "& > .mb-dt-cell-content": {
          width: "100%",
        },
      }}
    >
      {children}
    </Box>
    <HStack
      color="gray.400"
      spacing="4px"
      onClick={(e) => {
        // prevent click from propagating to the header cell
        e.stopPropagation();
      }}
    >
      {actions.showEdit && (
        <CellEditButton
          ariaLabel="edit"
          icon={EditIcon}
          onClick={(event) =>
            onButtonClick?.({
              tableContext,
              columnContext,
              row,
              cell,
              buttonType: "edit",
              event,
            })
          }
        />
      )}
      {actions.showDelete && (
        <CellEditButton
          ariaLabel="delete"
          icon={TrashIcon}
          onClick={(event) =>
            onButtonClick?.({
              tableContext,
              columnContext,
              row,
              cell,
              buttonType: "delete",
              event,
            })
          }
        />
      )}
    </HStack>
  </Flex>
);

const CellEditButton: React.FC<{
  ariaLabel: string;
  icon: React.ComponentType<IconProps>;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  color?: string;
}> = ({ ariaLabel, icon: IconComponent, onClick, color = "unset" }) => (
  <IconButton
    alignSelf="flex-end"
    aria-label={ariaLabel}
    variant="ghost"
    icon={<IconComponent width="16px" height="16px" />}
    color={color}
    size="xxs-icon"
    onClick={onClick}
  />
);
